import axios from 'axios';
import config from './config';

// Configuración base de Axios
const api = axios.create({
  baseURL: config.apiUrl,
  timeout: 30000, // Aumentado a 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token a las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    
    // Para errores 400 (Bad Request), pasar el error completo del servidor
    if (error.response?.status === 400) {
      return Promise.reject(error);
    }
    
    // Para errores 401/403, verificar si es un error de login o de token inválido
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Si es un error de login (no tiene token o el error viene del endpoint de login)
      if (!localStorage.getItem('token') || error.config?.url?.includes('/auth/login')) {
        // Pasar el error completo para que se maneje en el componente
        return Promise.reject(error);
      }
      
      // Si es un error de token inválido en otras rutas, redirigir al login
      if (!error.response?.data?.requiereAprobacion) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    
    if (error.response?.data?.error) {
      return Promise.reject(new Error(error.response.data.error));
    }
    return Promise.reject(new Error('Error de conexión'));
  }
);

// Servicios para expedientes
export const expedientesAPI = {
  // Obtener lista de expedientes
  getExpedientes: (params = {}) => 
    api.get('/expedientes', { params }),

    // Obtener expediente por ID
  getExpediente: (id) =>
    api.get(`/expedientes/${id}`),

  // Crear expediente
  createExpediente: (data) =>
    api.post('/expedientes', data),

  // Actualizar expediente
  updateExpediente: (id, data) =>
    api.patch(`/expedientes/${id}`, data),

  // Eliminar expediente
  deleteExpediente: (id) => 
    api.delete(`/expedientes/${id}`),

  // Restaurar expediente
  restoreExpediente: (id) => 
    api.patch(`/expedientes/${id}/restore`),

  // Obtener clientes
  getClientes: () => 
    api.get('/clientes'),

    // Crear nuevo cliente
  createCliente: (data) =>
    api.post('/clientes', data),

  // Actualizar cliente
  updateCliente: (id, data) =>
    api.patch(`/clientes/${id}`, data),

  // Eliminar cliente
  deleteCliente: (id) =>
    api.delete(`/clientes/${id}`),
};

// Servicios para partes
export const partesAPI = {
  // Obtener partes de un expediente
  getPartes: (expedienteId) => 
    api.get(`/partes/expediente/${expedienteId}`),

  // Crear parte
  createParte: (expedienteId, data) => 
    api.post(`/partes/expediente/${expedienteId}`, data),

  // Actualizar parte
  updateParte: (id, data) => 
    api.patch(`/partes/${id}`, data),

  // Eliminar parte
  deleteParte: (id) => 
    api.delete(`/partes/${id}`),

  // Marcar parte como completada
  completarParte: (id) => 
    api.patch(`/partes/${id}/completar`),

  // Marcar parte como pendiente
  marcarPendiente: (id) => 
    api.patch(`/partes/${id}/pendiente`),
};

// Servicios para abonos
export const abonosAPI = {
  // Obtener abonos de un expediente
  getAbonos: (expedienteId) => 
    api.get(`/abonos/expediente/${expedienteId}`),

  // Crear abono
  createAbono: (expedienteId, data) => 
    api.post(`/abonos/expediente/${expedienteId}`, data),

  // Crear abono con evidencia
  createAbonoConEvidencia: (expedienteId, formData) => 
    api.post(`/abonos/expediente/${expedienteId}/con-evidencia`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // 🆕 Crear abono con recibo automático
  createAbonoConRecibo: (expedienteId, formData) => 
    api.post(`/abonos/expediente/${expedienteId}/con-recibo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // 🆕 Regenerar recibo de un abono existente
  regenerarRecibo: (abonoId) => 
    api.post(`/abonos/${abonoId}/regenerar-recibo`),

  // Actualizar abono
  updateAbono: (id, data) => 
    api.patch(`/abonos/${id}`, data),

  // Eliminar abono
  deleteAbono: (id) => 
    api.delete(`/abonos/${id}`),
};

// Servicios para archivos
export const archivosAPI = {
  // Obtener archivos de un expediente
  getArchivos: (expedienteId) => 
    api.get(`/archivos/expediente/${expedienteId}`),

  // Subir archivos
  uploadArchivos: (expedienteId, formData) => 
    api.post(`/archivos/expediente/${expedienteId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Eliminar archivo
  deleteArchivo: (id) => 
    api.delete(`/archivos/${id}`),

  // Descargar archivo
  downloadArchivo: (id) => 
    api.get(`/archivos/${id}/descargar`, { responseType: 'blob' }),
};

// Servicios para tipos
export const tiposAPI = {
  // Obtener tipos
  getTipos: () =>
    api.get('/tipos'),

  // Crear tipo
  createTipo: (data) =>
    api.post('/tipos', data),

  // Actualizar tipo
  updateTipo: (id, data) =>
    api.patch(`/tipos/${id}`, data),

  // Eliminar tipo
  deleteTipo: (id) =>
    api.delete(`/tipos/${id}`),
};

// Servicios para clientes
export const clientesAPI = {
  // Obtener clientes
  getClientes: () =>
    api.get('/clientes'),

  // Obtener cliente por ID
  getCliente: (id) =>
    api.get(`/clientes/${id}`),

  // Crear cliente
  createCliente: (data) =>
    api.post('/clientes', data),

  // Actualizar cliente
  updateCliente: (id, data) =>
    api.patch(`/clientes/${id}`, data),

  // Eliminar cliente
  deleteCliente: (id) =>
    api.delete(`/clientes/${id}`),
};

// Servicios para exportación
export const exportAPI = {
  // Exportar expedientes
  exportarExpedientes: (options) =>
    api.post('/export/expedientes', options, {
      responseType: 'blob'
    }),
};

// Servicios para control de versiones
export const versionesAPI = {
  // Obtener versiones de un archivo
  getVersiones: (archivoId) =>
    api.get(`/versiones/archivo/${archivoId}`),

  // Crear nueva versión
  createVersion: (archivoId, formData) =>
    api.post(`/versiones/archivo/${archivoId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Revertir a una versión anterior
  revertVersion: (archivoId, versionId) =>
    api.post(`/versiones/archivo/${archivoId}/revertir/${versionId}`),

  // Descargar una versión específica
  downloadVersion: (archivoId, versionId) =>
    api.get(`/versiones/archivo/${archivoId}/descargar/${versionId}`, {
      responseType: 'blob'
    }),

  // Eliminar una versión
  deleteVersion: (archivoId, versionId) =>
    api.delete(`/versiones/archivo/${archivoId}/eliminar/${versionId}`),

  // Obtener estadísticas de versiones
  getEstadisticas: () =>
    api.get('/versiones/estadisticas'),
};

// Servicios para explorador de carpetas
export const foldersAPI = {
  // Obtener estructura de carpetas de un expediente
  getExpedienteFolders: (expedienteId) =>
    api.get(`/folders/expediente/${expedienteId}`),

  // Obtener archivos de una parte específica
  getParteFiles: (parteId) =>
    api.get(`/folders/parte/${parteId}`),

  // Subir archivo a una parte específica
  uploadToParte: (parteId, formData) =>
    api.post(`/folders/parte/${parteId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Eliminar archivo de una parte
  deleteParteFile: (parteId, fileName) =>
    api.delete(`/folders/parte/${parteId}/file/${fileName}`),
};

// Servicios para autenticación
export const authAPI = {
  // Login
  login: (data) =>
    api.post('/auth/login', data),

  // Registro
  register: (data) =>
    api.post('/auth/register', data),

  // Obtener información del usuario actual
  getMe: () =>
    api.get('/auth/me'),

  // Logout
  logout: () =>
    api.post('/auth/logout'),

  // Actualizar perfil
  updateProfile: (profileData) =>
    api.patch('/auth/profile', profileData),

  // Cambiar contraseña
  changePassword: (data) =>
    api.post('/auth/change-password', data),
};

// Servicios para dashboard
export const dashboardAPI = {
  // Obtener KPIs
  getKPIs: () => 
    api.get('/dashboard/kpis'),

  // Obtener deudas por cliente
  getDeudasPorCliente: (params = {}) => 
    api.get('/dashboard/deudas', { params }),

  // Obtener vencimientos
  getVencimientos: (dias = 7) => 
    api.get('/dashboard/vencimientos', { params: { dias } }),

  // Obtener resumen ejecutivo
  getResumen: () => 
    api.get('/dashboard/resumen'),

  // Obtener ingresos mensuales
  getIngresosMensuales: () => 
    api.get('/dashboard/ingresos-mensuales'),
};

// Función para copiar al portapapeles
export const copyToClipboard = async (text) => {
  try {
    // Intentar usar la API moderna del navegador
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback para navegadores más antiguos o contextos no seguros
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        document.body.removeChild(textArea);
        console.error('Error con execCommand:', err);
        return false;
      }
    }
  } catch (err) {
    console.error('Error al copiar al portapapeles:', err);
    return false;
  }
};

// Función para formatear fechas
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

// Función para formatear hora
export const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Función para formatear moneda
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN'
  }).format(amount);
};

// Función para obtener clase de porcentaje
export const getPorcentajeClass = (porcentaje) => {
  if (porcentaje < 40) return 'porcentaje-bajo';
  if (porcentaje < 100) return 'porcentaje-medio';
  return 'porcentaje-alto';
};

// Servicios para aprobaciones
export const approvalsAPI = {
  // Obtener solicitudes pendientes
  getPendingApprovals: () =>
    api.get('/approvals/pending'),

  // Obtener historial de solicitudes
  getApprovalHistory: () =>
    api.get('/approvals/history'),

  // Obtener estadísticas de aprobaciones
  getApprovalStats: () =>
    api.get('/approvals/stats'),

  // Aprobar solicitud
  approveRequest: (solicitudId, data) =>
    api.post(`/approvals/${solicitudId}/approve`, data),

  // Rechazar solicitud
  rejectRequest: (solicitudId, data) =>
    api.post(`/approvals/${solicitudId}/reject`, data),
};

// Servicios para sugerencias
export const sugerenciasAPI = {
  // Obtener todas las sugerencias (solo JEFE)
  obtenerTodas: () =>
    api.get('/sugerencias'),

  // Obtener mis sugerencias
  obtenerMisSugerencias: () =>
    api.get('/sugerencias/mis-sugerencias'),

  // Crear nueva sugerencia
  crear: (data) =>
    api.post('/sugerencias', data),

  // Subir archivo para sugerencia
  subirArchivo: (sugerenciaId, formData) =>
    api.post(`/sugerencias/${sugerenciaId}/archivo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Actualizar estado de sugerencia (solo JEFE)
  actualizarEstado: (id, data) =>
    api.patch(`/sugerencias/${id}/estado`, data),

  // Obtener estadísticas (solo JEFE)
  obtenerEstadisticas: () =>
    api.get('/sugerencias/estadisticas'),
};

// Servicios para asistencia
export const asistenciaAPI = {
  // Obtener registro actual del día
  getRegistroActual: (userId) =>
    api.get(`/asistencia/registro-actual/${userId}`),

  // Registrar check-in
  checkIn: (userId, data) =>
    api.post(`/asistencia/check-in/${userId}`, data),

  // Registrar check-out
  checkOut: (registroId, data) =>
    api.put(`/asistencia/check-out/${registroId}`, data),

  // Obtener historial de asistencia
  getHistorial: (userId, params = {}) =>
    api.get(`/asistencia/historial/${userId}`, { params }),

  // Obtener estadísticas de asistencia
  getEstadisticas: (userId) =>
    api.get(`/asistencia/estadisticas/${userId}`),

  // Obtener registros incompletos (solo admin/jefe)
  getRegistrosIncompletos: (params = {}) =>
    api.get('/asistencia/registros-incompletos', { params }),

  // Agregar hora de salida a registro incompleto (solo admin/jefe)
  agregarSalida: (registroId, data) =>
    api.put(`/asistencia/agregar-salida/${registroId}`, data),

  // Agregar hora de entrada a registro incompleto (solo admin/jefe)
  agregarEntrada: (registroId, data) =>
    api.put(`/asistencia/agregar-entrada/${registroId}`, data),

  // Crear registro completo cuando no marcaron nada (solo admin/jefe)
  crearRegistroCompleto: (data) =>
    api.post('/asistencia/crear-registro-completo', data),

  // Obtener resumen de sanciones (solo admin/jefe)
  getSanciones: (params = {}) =>
    api.get('/asistencia/sanciones', { params }),

  // Exportar reporte de asistencia a Excel (solo admin/jefe)
  exportarAsistenciaExcel: (params = {}) =>
    api.get('/asistencia/exportar-excel', { 
      params,
      responseType: 'blob' // Importante para descargar archivos
    }),

  // Obtener usuarios disponibles para crear registros (solo admin/jefe)
  getUsuariosDisponibles: () =>
    api.get('/asistencia/usuarios-disponibles'),

  // Obtener lista de usuarios para reportes (solo admin/jefe)
  getUsuarios: () =>
    api.get('/asistencia/usuarios'),

  // Obtener reporte de asistencia (solo admin/jefe)
  getReporte: (params = {}) =>
    api.get('/asistencia/reporte', { params }),
};

export default api;
